import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook for real-time messaging via WebSocket.
 * Handles connection, reconnection, typing indicators, and incoming messages.
 * Includes retry logic for token availability and robust error handling.
 *
 * @param {Object} options
 * @param {Function} options.onNewMessage - Called when a new message arrives: (conversationId, message) => void
 * @param {Function} options.onTyping - Called when typing status changes: (conversationId, senderId, senderName, isTyping) => void
 * @param {Function} options.onMessagesRead - Called when messages are read: (conversationId, readBy) => void
 * @param {string} options.activeConversationId - Currently open conversation ID (for typing indicators)
 */
export function useMessagingSocket({ onNewMessage, onTyping, onMessagesRead, activeConversationId } = {}) {
  const { getAuthToken } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const tokenRetryRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const [isConnected, setIsConnected] = useState(false);
  const maxReconnectAttempts = 10;
  const maxTokenRetries = 20; // Retry for up to ~10 seconds waiting for token
  const tokenRetryCount = useRef(0);
  const callbacksRef = useRef({ onNewMessage, onTyping, onMessagesRead });
  const mountedRef = useRef(true);

  // Keep callbacks ref updated
  useEffect(() => {
    callbacksRef.current = { onNewMessage, onTyping, onMessagesRead };
  }, [onNewMessage, onTyping, onMessagesRead]);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    const token = getAuthToken();
    if (!token) {
      // Token not ready yet — retry after a short delay
      if (tokenRetryCount.current < maxTokenRetries) {
        tokenRetryCount.current++;
        console.log(`[WS] Token not available, retrying in 500ms (attempt ${tokenRetryCount.current}/${maxTokenRetries})`);
        tokenRetryRef.current = setTimeout(() => {
          if (mountedRef.current) connect();
        }, 500);
      } else {
        console.warn('[WS] Token not available after max retries. WebSocket will not connect.');
      }
      return;
    }

    // Token obtained — reset retry counter
    tokenRetryCount.current = 0;

    // Close existing connection
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    // Build WebSocket URL — check both env var names
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;
    let host;
    if (apiUrl) {
      try {
        host = new URL(apiUrl).host;
      } catch {
        host = window.location.hostname + ':8000';
      }
    } else {
      host = window.location.hostname + ':8000';
    }
    const wsUrl = `${protocol}//${host}/ws?token=${encodeURIComponent(token)}`;

    console.log(`[WS] Connecting to ${protocol}//${host}/ws`);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        console.log('[WS] Connected successfully');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'new_message':
              console.log('[WS] New message received for conversation:', data.conversationId);
              callbacksRef.current.onNewMessage?.(data.conversationId, data.message);
              break;
            case 'typing':
              callbacksRef.current.onTyping?.(
                data.conversationId, data.senderId, data.senderName, data.isTyping
              );
              break;
            case 'messages_read':
              callbacksRef.current.onMessagesRead?.(data.conversationId, data.readBy);
              break;
            case 'pong':
              // Heartbeat response — connection alive
              break;
            default:
              break;
          }
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        console.log('[WS] Disconnected:', event.code, event.reason);
        setIsConnected(false);

        // Don't reconnect if intentionally closed or auth failure
        if (event.code === 4001 || event.code === 1000) return;

        // Exponential backoff reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS] Connection error');
        // onclose will handle reconnection
      };
    } catch (err) {
      console.error('[WS] Failed to create WebSocket:', err);
      // Retry connection after delay
      if (reconnectAttempts.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current++;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    }
  }, [getAuthToken]);

  // Connect on mount and whenever connect function changes (token becomes available)
  useEffect(() => {
    mountedRef.current = true;
    connect();

    // Heartbeat to keep connection alive (every 25s — slightly less than server timeout)
    const heartbeat = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);

    return () => {
      mountedRef.current = false;
      clearInterval(heartbeat);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (tokenRetryRef.current) clearTimeout(tokenRetryRef.current);
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [connect]);

  // Notify server which conversation user is viewing
  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'viewing',
        conversationId: activeConversationId || null
      }));
    }
  }, [activeConversationId]);

  /**
   * Send a typing indicator to the other user.
   * Automatically clears after 3 seconds of inactivity.
   */
  const sendTypingIndicator = useCallback((conversationId, isTyping = true) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversationId,
        isTyping
      }));

      // Auto-clear typing after 3 seconds
      if (isTyping) {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'typing',
              conversationId,
              isTyping: false
            }));
          }
        }, 3000);
      }
    }
  }, []);

  return {
    isConnected,
    sendTypingIndicator
  };
}
